import React from 'react';
import { Icon } from 'tabler-icons-react';
import type { ButtonProps } from '@mantine/core';

export const TABLE_NAVIGATE_ACTION = {
  EDIT: 'edit',
  DELETE: 'delete',
  VIEW: 'view',
  UPDATE: 'update',
};

export const actionTypes = {
  CLEAR_SEARCH_PARAM: 'CLEAR_SEARCH_PARAM',
  REMOVE_ALL_FILTER: 'REMOVE_ALL_FILTER',
  RESET_CHECK_TO: 'RESET_CHECK_TO',
  RESET_FILTER: 'RESET_FILTER',
  RESET_INITIAL_STATE: 'RESET_INITIAL_STATE',
  RESET_SORT: 'RESET_SORT',
  SET_CHECK: 'SET_CHECK',
  SET_CHECK_ALL: 'SET_CHECK_ALL',
  SET_FILTER: 'SET_FILTER',
  SET_ITEMS_PER_PAGE: 'SET_ITEMS_PER_PAGE',
  SET_NAVIGATION: 'SET_NAVIGATION',
  SET_NO_PAGINATION: 'SET_NO_PAGINATION',
  SET_PATH: 'SET_PATH',
  SET_SORT: 'SET_SORT',
  UNCHECK: 'UNCHECK',
  UNCHECK_ALL: 'UNCHECK_ALL',
} as const;

export interface ICustomAction {
  toolTipLabel: React.ReactNode;
  isDisabled: boolean;
  callBack: (object?: any) => void;
  icon: React.ReactNode;
  confirmationLabel?: string;
}

interface IBaseAction {
  buttonLabel?: string | React.ReactNode;
  isDisabled: boolean;
  isHidden?: boolean;
  callBack: (key?: string, object?: any) => void;
  icon?: Icon;
  variant?: ButtonProps['variant'];
  displayOptionalText?: boolean;
  optionalText?: React.ReactNode;
}

interface IActionWithoutTooltip extends IBaseAction {
  toolTipLabel?: undefined;
  ariaLabelText?: string;
}

interface IActionWithTooltip extends IBaseAction {
  toolTipLabel: React.ReactNode;
  ariaLabelText: string;
}

export type IAction = IActionWithoutTooltip | IActionWithTooltip;

export interface IDefaultValues {
  basePath: string;
  itemsPerPage: number;
  isPagination: boolean;
  pageSizeOptions?: number[];
  queryKey: string;
  queryFunc: (url: any) => any;
  pathPrefix: string;
  deleteAction: {
    title?: string;
    message?: string;
    queryFunc: (url: any) => any;
  };
  updateAction: {
    title?: string;
    message?: string;
    queryFunc: (data: any, iri: string) => any;
  };
  defaultSort?: string;
  defaultSearchParams?: string;
}

export interface INavigationPagination {
  currentPage?: number;
  totalItems: number;
  first?: string | null;
  previous?: string | null;
  next?: string | null;
  last?: string | null;
}

export interface IInitialState {
  path: string;
  filters: any[];
  searchParamFilters: any[];
  sorts: { property: string; value: string }[];
  basePath: string;
  isPagination: boolean;
  itemsPerPage: number;
  navigation?: INavigationPagination;
  pageSizeOptions: number[];
  rowChecked: string[];
}

export interface IFilter {
  property: string;
  value: any;
}

export interface ITextOnly {
  displayWarning: boolean;
  warningText: React.ReactNode;
}

export interface IOption {
  replaceOption: { index: number; transform: (entry: string) => string }[];
  excludeOption: number[];
}

export interface ExcelDownloadButtonProps {
  currentPageDataUrl?: string;
  allPageDataUrl?: string;
  fileTitle: string;
  getTranslatedHeader?: (rawHeader: string, headerIndex: number, extraOption: IOption) => string;
  isPOC?: boolean;
}

export interface IExcelDownloadButton extends ExcelDownloadButtonProps {
  excelButtonComponent: React.ComponentType<ExcelDownloadButtonProps>;
}

export interface ExcelExportColumn {
  property: string;
  headerLabel: string | ((match: string) => string);
  formatter?: (value: string, row?: any) => string | number;
}

export interface ExcelExportGroupedColumn {
  propertyPrefix: string;
  columns: ExcelExportColumn[];
}

export interface ExcelDownloadButton2Props {
  currentPageDataUrl?: string;
  allDataUrl?: string;
  fileTitle: string;
  exportStructure: (ExcelExportColumn | ExcelExportGroupedColumn)[];
  exportAllDataStructure?: (ExcelExportColumn | ExcelExportGroupedColumn)[];
}

export interface IExcelDownloadButton2 extends ExcelDownloadButton2Props {
  excelButtonComponent2: React.ComponentType<ExcelDownloadButton2Props>;
}

export interface IActionButton {
  actions: (IAction | ITextOnly | IExcelDownloadButton | IExcelDownloadButton2)[];
}
